import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Platform, ScrollView, View } from "react-native";
import {
  displayErrorMessage,
  Message,
  theme,
} from "@clearabee/ui-library-native";
import {
  getItemDescendants,
  instance,
  readAddresses,
  useCatalogue,
} from "@clearabee/ui-sdk";
import { useRouter } from "lib/hooks/useRouter";
import { Button } from "components/Core/Button/Button";
import { styles } from "./BinCollection.styles";
import { ProductList } from "./ProductList";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { Layout } from "components/Layout/Layout";
import { Form, useForm } from "lib/hooks/useForm";
import { getValidation } from "./BinCollection.validation";
import { useAnalytics } from "lib/hooks/useAnalytics";
import { useMutation } from "react-query";
import {
  PostcodePricing,
  Values,
} from "components/PostcodePricing/PostcodePricing";
import { PostcodeResultMessage } from "components/PostcodeResultMessage/PostcodeResultMessage";
import { SEO } from "components/Seo/Seo";

interface BinCollectionProps {
  postcode: string | undefined;
}

const categorySku = "BINCOL";

export const BinCollection = ({
  postcode: postcodeParam,
}: BinCollectionProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { navigate, replace } = useRouter();
  const { defaultCatalogue, setCatalogue, getCatalogue } = useCatalogue();
  const [postcode, setPostcode] = useState(postcodeParam ?? "");
  const catalogue = getCatalogue(postcode) || defaultCatalogue;
  const { ecommerce } = useAnalytics();
  const service = catalogue.items.find(({ sku }) => sku === categorySku);
  const productsRef = useRef(
    catalogue.items.filter(({ parentSku }) => parentSku === categorySku),
  );
  const [invalidPostcode, setInvalidPostcode] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  const serviceProducts = getItemDescendants(categorySku, catalogue.items);

  const { isLoading, mutate: getPostcodeCatalogue } = useMutation(
    (postcode: string) =>
      instance.catalogues.getCatalogue("hwa", {
        params: { postcode, flatten: true },
      }),

    {
      onSuccess: (response, mutatePostcode) => {
        setCatalogue(mutatePostcode, response.data);
        scrollRef.current?.scrollTo({ y: 650, animated: true });
      },
    },
  );

  const form = useForm({
    defaultValues: { products: {} },
    reValidateMode: "onChange",
    validationSchema: getValidation(productsRef.current),
    onSubmit: (values) => {
      const selectedItems = Object.entries(values.products).filter(
        ([, qty]) => !!qty,
      );
      navigate({
        routeName: "BulkAdd",
        params: {
          categorySku,
          products: selectedItems.map((entry) => entry.join(",")).join(","),
          postcode,
        },
      });
    },
  });
  const {
    submitForm,
    formState: { errors, isValid },
    setValue,
  } = form;

  useEffect(() => {
    ecommerce.viewItemList(
      serviceProducts,
      `${categorySku} Product List`,
      `${categorySku}_product_list`,
    );
  }, []);

  useEffect(() => {
    if (!!errors && isValid) {
      form.clearErrors();
    }
  }, [isValid]);

  const handleSubmit = async ({ postcode }: Values) => {
    try {
      const { addresses } = await readAddresses(postcode);
      if (addresses.length) {
        setInvalidPostcode(false);
        setPostcode(postcode);
        if (!getCatalogue(postcode)) {
          getPostcodeCatalogue(postcode);
        }
      } else {
        setInvalidPostcode(true);
        setPostcode("");
      }
    } catch (e) {
      setInvalidPostcode(true);
      setPostcode("");
    }

    setValue("products", {});

    const route = {
      routeName: "ServicePage",
      params: {
        categorySku,
        postcode: postcode || undefined,
      },
      shallow: true,
    } as const;
    if (Platform.OS === "web") {
      replace(route);
    } else {
      navigate(route);
    }
  };

  if (!service) return <ErrorPage />;

  return (
    <>
      <SEO
        title={`${translate("hwa.screens.selectProduct.title")} - ${
          service?.title ?? categorySku
        }`}
      />
      <Layout
        title={translate("hwa.screens.selectProduct.title")}
        backTo={{ routeName: "Book" }}
        contentWidth="xlarge"
        scrollRef={scrollRef}
      >
        <View style={{ zIndex: 1 }}>
          <PostcodePricing
            postcode={postcode}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isExtended={false}
            categorySku={categorySku}
          />
          <PostcodeResultMessage
            postcode={postcode}
            hasResults={!!serviceProducts.length}
            invalidPostcode={invalidPostcode}
          />
        </View>

        <View
          style={{
            maxWidth: theme.screens.large,
            margin: "auto",
            width: "100%",
          }}
        >
          {!!Object.keys(errors).length && (
            <View
              style={{
                alignItems: "center",
              }}
            >
              {displayErrorMessage(Object.values(errors), (props) => (
                <Message
                  {...props}
                  type="error"
                  color="light"
                  background
                  styles={{
                    margin: "auto",
                    backgroundColor: theme.colors.negative.base,
                  }}
                />
              ))}
            </View>
          )}

          <Form state={form}>
            <ProductList
              products={serviceProducts}
              postcode={postcode}
              isLoading={isLoading}
            />
          </Form>

          <Button
            color="warning"
            size="medium"
            styles={styles.nextButton}
            onClick={submitForm}
          >
            {translate("hwa.screens.selectProduct.nextStep")}
          </Button>
        </View>
      </Layout>
    </>
  );
};
